import { STORAGE_KEYS } from '../constants';

/**
 * Set an item in local storage
 * @param key The storage key
 * @param value The value to store
 */
export const setStorageItem = (key: string, value: any): void => {
  try {
    const serializedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error('Error setting localStorage item:', error);
  }
};

/**
 * Get an item from local storage
 * @param key The storage key
 * @param defaultValue Default value if key doesn't exist
 * @returns The stored value or default value
 */
export const getStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    try {
      // Try to parse as JSON
      return JSON.parse(item);
    } catch {
      // If not valid JSON, return as is
      return item as unknown as T;
    }
  } catch (error) {
    console.error('Error getting localStorage item:', error);
    return defaultValue;
  }
};

/**
 * Remove an item from local storage
 * @param key The storage key
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage item:', error);
  }
};

/**
 * Clear all items from local storage
 */
export const clearStorage = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Get the stored auth token
 * @returns The auth token or null
 */
export const getAuthToken = (): string | null => {
  return getStorageItem<string | null>(STORAGE_KEYS.AUTH_TOKEN, null);
};

/**
 * Set the auth token in storage
 * @param token The auth token
 */
export const setAuthToken = (token: string): void => {
  setStorageItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Remove the auth token from storage
 */
export const removeAuthToken = (): void => {
  removeStorageItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get the stored user data
 * @returns The user data or null
 */
export const getUserData = (): any | null => {
  return getStorageItem(STORAGE_KEYS.USER_DATA, null);
};

/**
 * Set the user data in storage
 * @param userData The user data
 */
export const setUserData = (userData: any): void => {
  setStorageItem(STORAGE_KEYS.USER_DATA, userData);
};

/**
 * Remove the user data from storage
 */
export const removeUserData = (): void => {
  removeStorageItem(STORAGE_KEYS.USER_DATA);
};
